import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Clock, Eye, Filter, X, ArrowLeft, BookOpen } from 'lucide-react';
import { blogs } from '../data/blogs';
import Header from './Header';
import Footer from './Footer';

const BlogsPage = () => {
  const [filter, setFilter] = useState('all');
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [hoveredPost, setHoveredPost] = useState(null);

  // Use static data instead of API call
  const blogsData = blogs;

  const categories = ['all', ...new Set(blogsData.map(b => b.category))];
  const filteredPosts = filter === 'all' ? blogsData : blogsData.filter(b => b.category === filter);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleReadMore = (blog) => {
    setSelectedBlog(blog);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <Header />
      
      {/* Blogs Page Content */}
      <div className="pt-20 pb-20">
        {/* Page Header */}
        <div className="max-w-7xl mx-auto px-6 mb-12">
          <div className="text-center mb-8">
            <Button 
              onClick={() => window.location.href = '/'}
              className="btn-base btn-secondary mb-6"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to Portfolio
            </Button>
            
            <h1 className="heading-1 text-text-primary mb-4">
              All Blog Posts
            </h1>
            <p className="body-large text-text-secondary max-w-2xl mx-auto">
              Explore insights on leadership, technology, AI, and investment management 
              from my professional journey and industry expertise.
            </p>
          </div>

          {/* Filter Buttons */}
          <div className="flex justify-center mb-8">
            <div className="flex flex-wrap gap-3 p-1 bg-white rounded-xl shadow-sm border border-gray-200">
              {categories.map((category) => (
                <Button
                  key={category}
                  onClick={() => setFilter(category)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    filter === category
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                  variant="ghost"
                >
                  <Filter size={16} className="mr-2" />
                  {category === 'all' ? 'All Posts' : category}
                </Button>
              ))}
            </div>
          </div>
        </div>

        {/* Blogs Grid */}
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredPosts.map((post) => (
              <Card 
                key={post.id}
                className={`group glass-card overflow-hidden transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl flex flex-col h-full ${
                  hoveredPost === post.id ? 'ring-2 ring-brand-primary/30' : ''
                }`}
                onMouseEnter={() => setHoveredPost(post.id)}
                onMouseLeave={() => setHoveredPost(null)}
              >
                {/* Post Image */}
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={post.image || '/api/placeholder/400/240'}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    onError={(e) => {
                      e.target.src = '/api/placeholder/400/240';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-white/90 via-transparent to-transparent"></div>
                  
                  {/* Category Badge */}
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-indigo-600 text-white font-medium">
                      {post.category}
                    </Badge>
                  </div>

                  {/* Hover Actions */}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90">
                    <Button 
                      onClick={() => handleReadMore(post)}
                      className="btn-base btn-indigo btn-sm"
                    >
                      <Eye size={16} className="mr-2" />
                      Read More
                    </Button>
                  </div>
                </div>

                {/* Post Content */}
                <div className="p-6 flex-1 flex flex-col">
                  {/* Title */}
                  <h3 className="heading-5 text-text-primary mb-3 group-hover:text-gradient transition-colors duration-300 line-clamp-2">
                    {post.title}
                  </h3>

                  {/* Excerpt */}
                  <div 
                    className="body-small text-text-secondary mb-4 line-clamp-3 rich-text-content flex-grow"
                    dangerouslySetInnerHTML={{ __html: post.excerpt }}
                  />

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {post.tags?.map((tag, index) => (
                      <Badge key={index} className="bg-gray-100 text-gray-700 px-2 py-1 text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>

                  {/* Meta Info */}
                  <div className="flex items-center justify-between text-sm text-text-muted mt-auto">
                    <div className="flex items-center">
                      <Clock size={14} className="mr-1" />
                      {post.readTime}
                    </div>
                    <span>{formatDate(post.updated_at || post.publishDate)}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          {/* Empty State */}
          {filteredPosts.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <BookOpen size={48} className="mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No blog posts found</h3>
              <p className="text-gray-500">Try selecting a different category</p>
            </div>
          )}
        </div>
      </div>

      {/* Blog Detail Modal */}
      {selectedBlog && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
            {/* Close Button */}
            <Button
              onClick={() => setSelectedBlog(null)}
              className="absolute top-6 right-6 z-10 w-12 h-12 rounded-full bg-white/90 hover:bg-white shadow-lg transition-all duration-200"
              variant="ghost"
            >
              <X size={20} className="text-gray-700" />
            </Button>

            {/* Modal Content */}
            <div className="p-8">
              {/* Article Header */}
              <div className="mb-8">
                <div className="flex flex-wrap gap-3 mb-4">
                  <Badge className="bg-indigo-600 text-white font-medium">
                    {selectedBlog.category}
                  </Badge>
                  <div className="flex items-center text-sm text-text-muted">
                    <Clock size={16} className="mr-2" />
                    {selectedBlog.readTime}
                  </div>
                </div>
                
                <h1 className="text-4xl font-bold text-text-primary mb-4">
                  {selectedBlog.title}
                </h1>
                
                <div className="flex items-center text-text-muted mb-6">
                  <span>Updated on {formatDate(selectedBlog.updated_at || selectedBlog.publishDate)}</span>
                </div>
              </div>

              {/* Featured Image */}
              {selectedBlog.image && (
                <div className="mb-8">
                  <img 
                    src={selectedBlog.image} 
                    alt={selectedBlog.title}
                    className="w-full h-80 object-cover rounded-2xl shadow-lg"
                    onError={(e) => {
                      e.target.src = '/api/placeholder/600/320';
                    }}
                  />
                </div>
              )}

              {/* Article Excerpt */}
              <div className="mb-8">
                <div 
                  className="text-xl text-text-secondary font-medium leading-relaxed rich-text-content"
                  dangerouslySetInnerHTML={{ __html: selectedBlog.excerpt }}
                />
              </div>

              {/* Article Content */}
              <div className="prose prose-xl max-w-none">
                <div 
                  className="space-y-6 text-text-secondary leading-relaxed text-lg rich-text-content"
                  dangerouslySetInnerHTML={{ __html: selectedBlog.content }}
                />
              </div>

              {/* Tags */}
              {selectedBlog.tags && selectedBlog.tags.length > 0 && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-semibold text-text-primary mb-3">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedBlog.tags.map((tag, index) => (
                      <Badge key={index} className="bg-gray-100 text-gray-700 px-3 py-1">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default BlogsPage;